import {
  Button, Icon, Input, Menu, MenuButton, MenuItem, MenuList, VStack, useColorModeValue,
} from "@chakra-ui/react";
import { useMemo, useState } from "react";
import { IconType } from "react-icons";
import { FiChevronDown, FiX } from "react-icons/fi";

export interface FilterChipOption {
  label: string;
  value: string;
}

interface FilterChipMenuProps {
  /** Trigger label shown when nothing is selected (e.g. "Assignee", "Stage"). */
  label: string;
  options: FilterChipOption[];
  value: string | undefined;
  onChange: (next: string | undefined) => void;
  /** Optional left icon when no value is selected. */
  icon?: IconType;
  /** Show a search input at the top of the dropdown. */
  searchable?: boolean;
  searchPlaceholder?: string;
  minWidth?: string;
  /** Cap on the dropdown body height before scrolling. */
  maxHeight?: string;
}

/**
 * Single-select filter chip with a dropdown menu. Visually matches
 * `FilterButton` (28px, `cdsIndigo.300` active, 13px medium) so it composes
 * cleanly in `FilterSortSearchBar` toolbar rows alongside `FilterButton`
 * instances.
 *
 * Use this for filter chips that need a dropdown (assignee, stage, role, etc.).
 * For form-field-shaped dropdowns (table headers, async server-side options),
 * keep using `components/Select/TableFilterSelect.tsx` — different visual
 * language for a different context.
 */
export default function FilterChipMenu({
  label, options, value, onChange,
  icon, searchable = false, searchPlaceholder = "Search...",
  minWidth = "200px", maxHeight = "260px",
}: FilterChipMenuProps) {
  const inactiveTextColor = useColorModeValue("gray.600", "gray.200");
  const activeBg = useColorModeValue("cdsIndigo.300", "cdsIndigo.500");
  const activeHoverBg = useColorModeValue("cdsIndigo.200", "cdsIndigo.400");
  const inactiveHoverBg = useColorModeValue("gray.50", "whiteAlpha.200");

  const [search, setSearch] = useState("");
  const selected = useMemo(
    () => (value ? options.find((o) => o.value === value) ?? null : null),
    [options, value],
  );
  const isActive = selected != null;

  const visibleOptions = useMemo(() => {
    if (!searchable || !search) return options;
    const q = search.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, search, searchable]);

  const clear = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    onChange(undefined);
    setSearch("");
  };

  return (
    <Menu isLazy onClose={() => setSearch("")}>
      <MenuButton
        as={Button}
        size="sm"
        variant="outline"
        colorScheme="gray"
        h="28px"
        minH="28px"
        px={2.5}
        fontSize="13px"
        fontWeight="medium"
        leftIcon={!isActive && icon ? <Icon as={icon} boxSize={3.5} color={inactiveTextColor} /> : undefined}
        rightIcon={
          isActive
            ? <Icon as={FiX} boxSize={3} onClick={clear} aria-label={`Clear ${label}`} />
            : <Icon as={FiChevronDown} boxSize={3} />
        }
        color={isActive ? "white" : inactiveTextColor}
        bg={isActive ? activeBg : undefined}
        _hover={{ bg: isActive ? activeHoverBg : inactiveHoverBg }}
      >
        {selected?.label ?? label}
      </MenuButton>
      <MenuList maxH={maxHeight} overflowY="auto" minW={minWidth} zIndex={10}>
        {searchable && (
          <VStack px={2} pb={1} spacing={0}>
            <Input
              placeholder={searchPlaceholder}
              size="sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              // Chakra Menu intercepts printable keys for type-to-navigate, which
              // would steal characters meant for this search field. Stop the events
              // before they bubble to the menu's key handler.
              onKeyDown={(e) => e.stopPropagation()}
              onKeyUp={(e) => e.stopPropagation()}
            />
          </VStack>
        )}
        {visibleOptions.length === 0 ? (
          <MenuItem isDisabled fontSize="sm">No matches</MenuItem>
        ) : visibleOptions.map((option) => (
          <MenuItem
            key={option.value}
            fontSize="sm"
            fontWeight={value === option.value ? "semibold" : "normal"}
            onClick={() => { onChange(option.value); setSearch(""); }}
          >
            {option.label}
          </MenuItem>
        ))}
      </MenuList>
    </Menu>
  );
}
